import { parseMessageData } from "@/lib/utils/parse-message-data";
import React, { useMemo } from "react";
import {
  Gamepad2,
  Zap,
  Brain,
  TrendingUp,
  Trophy,
  LucideIcon,
} from "lucide-react";

interface GameMessageData {
  type: string;
  gameType: string;
  gameSessionId: string;
  playerId: string;
  playerName: string;
  finalScore?: number;
  level?: number;
  maxLevel?: number;
}

interface GameMessageRendererProps {
  message: {
    customType: string;
    data?: string;
  };
}

interface GameConfig {
  name: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  borderColor: string;
  startMessage: string;
  getEndMessage: (score?: number, level?: number) => string;
  getCompliment: (score?: number, level?: number) => string | null;
}

const GameMessageRenderer: React.FC<GameMessageRendererProps> = ({
  message,
}) => {
  const gameData = useMemo(
    () => parseMessageData<GameMessageData>(message.data),
    [message.data],
  );

  if (!gameData || gameData.type !== "game") {
    return null;
  }

  const isGameStart = message.customType === "GAME_START";
  const isGameEnd = message.customType === "GAME_END";

  // Game-specific configurations
  const getGameConfig = (gameType: string): GameConfig => {
    switch (gameType) {
      case "tap_counter":
        return {
          name: "Tap Counter",
          icon: Zap,
          color: "#10B981",
          bgColor: "bg-emerald-50",
          borderColor: "border-emerald-100",
          startMessage: "is testing their reflexes",
          getEndMessage: (score?: number) => {
            if (!score) return "finished their tapping session";
            if (score < 10) return `tapped ${score} times - warming up!`;
            if (score < 25) return `achieved ${score} taps - getting there!`;
            if (score < 50) return `smashed ${score} taps - impressive!`;
            return `dominated with ${score} taps - lightning fast!`;
          },
          getCompliment: (score?: number) => {
            if (!score) return null;
            if (score >= 50) return "🔥 Speed demon!";
            if (score >= 25) return "⚡ Quick fingers!";
            if (score >= 10) return "👍 Nice pace!";
            return "🎯 Good start!";
          },
        };

      case "sequence_repeater":
        return {
          name: "Sequence Repeater",
          icon: Brain,
          color: "#8B5CF6",
          bgColor: "bg-purple-50",
          borderColor: "border-purple-100",
          startMessage: "is training their memory",
          getEndMessage: (score?: number, level?: number) => {
            if (!score && !level) return "finished their memory challenge";
            if (level && level <= 3)
              return `completed ${level} levels - getting started!`;
            if (level && level <= 7)
              return `mastered ${level} levels with ${score} points!`;
            if (level && level <= 15)
              return `conquered ${level} levels scoring ${score} points!`;
            return `dominated ${level || 0} levels with an amazing ${score} points!`;
          },
          getCompliment: (_score?: number, level?: number) => {
            if (!level) return null;
            if (level >= 15) return "🧠 Memory master!";
            if (level >= 10) return "🎯 Sharp mind!";
            if (level >= 5) return "💪 Good recall!";
            return "🌟 Nice try!";
          },
        };

      case "ascent":
        return {
          name: "Ascent",
          icon: TrendingUp,
          color: "#3B82F6",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-100",
          startMessage: "is climbing to new heights",
          getEndMessage: (score?: number) => {
            if (!score) return "finished their climb";
            if (score < 50) return `climbed ${score} points - warming up!`;
            if (score < 100) return `reached ${score} points - nice climb!`;
            if (score < 200) return `ascended to ${score} points - impressive!`;
            if (score < 300)
              return `soared to ${score} points - amazing height!`;
            return `conquered the peak with ${score} points - legendary!`;
          },
          getCompliment: (score?: number) => {
            if (!score) return null;
            if (score >= 300) return "🏔️ Peak performer!";
            if (score >= 200) return "🚀 Sky high!";
            if (score >= 100) return "⛰️ Great climber!";
            if (score >= 50) return "🧗 Good effort!";
            return "🎯 Nice try!";
          },
        };

      default:
        return {
          name: "Game",
          icon: Gamepad2,
          color: "#6366F1",
          bgColor: "bg-indigo-50",
          borderColor: "border-indigo-100",
          startMessage: "started playing",
          getEndMessage: (score?: number) =>
            score
              ? `finished with a score of ${score}`
              : "completed their game",
          getCompliment: () => "🎮 Well played!",
        };
    }
  };

  const gameConfig = getGameConfig(gameData.gameType);
  const GameIcon = gameConfig.icon;

  if (isGameStart) {
    return (
      <div className="flex items-center justify-center px-4 py-2">
        <div
          className={`flex max-w-[85%] flex-row items-center rounded-lg border p-4 shadow-sm ${gameConfig.bgColor} ${gameConfig.borderColor}`}
        >
          <div className="mr-3 rounded-full bg-white p-2 shadow-sm">
            <GameIcon size={20} color={gameConfig.color} />
          </div>
          <div className="flex-1">
            <div className="mb-1 text-sm font-semibold text-gray-900">
              {gameConfig.name} Started
            </div>
            <div className="text-xs leading-4 text-gray-600">
              {gameData.playerName} {gameConfig.startMessage}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isGameEnd) {
    const endMessage = gameConfig.getEndMessage(
      gameData.finalScore,
      gameData.level,
    );
    const compliment = gameConfig.getCompliment(
      gameData.finalScore,
      gameData.level,
    );

    return (
      <div className="flex items-center justify-center px-4 py-2">
        <div className="flex max-w-[85%] flex-row items-center rounded-lg border border-amber-100 bg-amber-50 p-4 shadow-sm">
          <div className="mr-3 rounded-full bg-white p-2 shadow-sm">
            <Trophy size={20} color="#F59E0B" />
          </div>
          <div className="flex-1">
            <div className="mb-1 text-sm font-semibold text-gray-900">
              Game Complete!
            </div>
            <div className="mb-1 text-xs leading-4 text-gray-600">
              {gameData.playerName} {endMessage}
            </div>
            {compliment && (
              <div className="text-xs font-medium text-amber-600">
                {compliment}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default GameMessageRenderer;
